import React, { useState, useEffect } from "react";
import { MdEdit } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getNavbarContent, updateNavbarContentApi } from "Services/allApi";

const NavbarContent = () => {
  const [navbarData, setNavbarData] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [formData, setFormData] = useState({
    favicon: null,
    nav_logo: null,
    nav_1_button_content: "",
    nav_1_button_link: "",
    nav_2_button_content: "",
    nav_2_button_link: "",
  });
  
  // State for image previews
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [navLogoPreview, setNavLogoPreview] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await getNavbarContent();
      const data = await response.data;
      setNavbarData(data[0]);
      setFormData(data[0]);
      setFaviconPreview(data[0].favicon); // Set initial preview to current favicon
      setNavLogoPreview(data[0].nav_logo); // Set initial preview to current nav logo
    } catch (error) {
      console.error("Error fetching navbar content:", error);
      toast.error("Failed to fetch navbar content!");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "file" ? files[0] : value,
    }));

    // Update preview for image files
    if (type === "file") {
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      if (name === "favicon") {
        setFaviconPreview(previewUrl);
      } else if (name === "nav_logo") {
        setNavLogoPreview(previewUrl);
      }
    }
  };

  const handleSave = async () => {
    const formDataToSend = new FormData();
    const fieldsToUpdate = [
      "favicon",
      "nav_logo",
      "nav_1_button_content",
      "nav_1_button_link",
      "nav_2_button_content",
      "nav_2_button_link",
    ];

    fieldsToUpdate.forEach((field) => {
      if (formData[field] !== navbarData[field]) {
        formDataToSend.append(field, formData[field]);
      }
    });

    if (!formDataToSend.has("favicon") && !formDataToSend.has("nav_logo") && !formDataToSend.has("nav_1_button_content") && !formDataToSend.has("nav_1_button_link") && !formDataToSend.has("nav_2_button_content") && !formDataToSend.has("nav_2_button_link")) {
      toast.info("No changes detected!");
      return;
    }

    try {
      const response = await updateNavbarContentApi(navbarData.id, formDataToSend);
      if (response.error) {
        toast.error(response.message);
      } else {
        // Update local state immediately
        setNavbarData((prevData) => ({
          ...prevData,
          ...formData,
        }));
        setEditModal(false);
        toast.success("Navbar updated successfully!");
      }
    } catch (error) {
      console.error("Error updating navbar content:", error);
      toast.error("Failed to update navbar content!");
    }
  };

  const handleCancel = ()=>{
    setEditModal(false)
    fetchData()
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-3 dark:bg-gray-800 dark:text-white">
      <h2 className="text-center text-3xl font-bold mb-5 mt-2">Navbar Content</h2>
      <hr className="mb-4" />

      {/* Navbar Preview */}
      {navbarData && (
        <div className="flex justify-between items-center bg-gray-100 p-4 rounded-lg shadow-md dark:bg-gray-700">
          <div className="flex items-center gap-3">
            <img src={navbarData.favicon} alt="Favicon" className="w-10 h-10 rounded-full" />
            <img src={navbarData.nav_logo} alt="Nav Logo" className="h-10" />
          </div>
          <div className="flex gap-4">
            <a href={navbarData.nav_1_button_link} className="bg-blue-600 text-white px-4 py-2 rounded-lg">
              {navbarData.nav_1_button_content}
            </a>
            <a href={navbarData.nav_2_button_link} className="bg-green-600 text-white px-4 py-2 rounded-lg">
              {navbarData.nav_2_button_content}
            </a>
          </div>
          <button
            onClick={() => setEditModal(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-700"
          >
            <MdEdit /> Edit
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-1/2 dark:bg-gray-800">
            <h3 className="text-xl font-semibold mb-4">Edit Navbar</h3>
            {/* File Inputs and Previews */}
             {/* Display new image preview */}
             {faviconPreview && (
                <img src={faviconPreview} alt="New Favicon Preview" className="mt-2 w-20 h-20 rounded-full" />
              )}
            <div className="mb-3">
              <label className="block font-medium">Favicon</label>
              <input
                type="file"
                name="favicon"
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg dark:bg-gray-700"
              />
            </div>
             {/* Display new image preview */}
             {navLogoPreview && (
                <img src={navLogoPreview} alt="New Nav Logo Preview" className="mt-2 w-20 h-20" />
              )}
            <div className="mb-3">
              <label className="block font-medium">Nav Logo</label>
              <input
                type="file"
                name="nav_logo"
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg dark:bg-gray-700"
              />
            </div>

            {["nav_1_button_content", "nav_1_button_link", "nav_2_button_content", "nav_2_button_link"].map((field) => (
              <div key={field} className="mb-3">
                <label className="block font-medium">{field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</label>
                <input
                  type="text"
                  name={field}
                  value={formData[field]}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-lg dark:bg-gray-700"
                />
              </div>
            ))}

            <div className="flex justify-end gap-2">
              <button onClick={handleCancel} className="px-4 py-2 bg-gray-500 text-white rounded-lg">
                Cancel
              </button>
              <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default NavbarContent;